import { useEffect, useState } from 'react';
import {
  fetchCandidates,
  fetchHeightmap,
  fetchPlaceCandidates,
  type CandidateView,
  type CandidatesView,
  type PlaceCandidateView,
  type PlaceCandidatesView,
} from '../api/missions';
import type { PlaceView } from '../api/catalog';
import { ca } from '../i18n/ca';
import { MissionMap } from './MissionMap';
import { dotSizeOf } from './placeDots';
import type { Heightmap } from './TerrainRelief';
import type { Selection } from './designModel';

/**
 * The survey's map of one type's choices on one terrain (2026-09-13): every
 * place the generator would take as the objective, the one already named, and
 * the ground it would stand the base on — the sites, the airfields, the best
 * open ground and the best water when an objective is named.
 *
 * It is the designer's own map with pins on it and nothing new drawn: the same
 * zoom, the same tiles from the Atlas when this host has them, the survey's
 * relief under them. What a click does is the caller's: the map step fills
 * "Munta la missió al voltant de" from an objective pin; the start step takes
 * a base pin, or any point on the ground, as where the night begins.
 *
 * `pick: 'place'` (release D, D11.8) is the designer's «Mou»: one place of a
 * generated mission and its kind's own candidates, from the mission's
 * candidates endpoint, the current place drawn as the numbered pin. Only a
 * candidate answers a click: a scripted place moves only among its kind's
 * candidates (the owner, 2026-09-28), never to a free point.
 */
export function CandidatesMap(props: CandidatesMapProps) {
  if (props.pick === 'place') return <PlaceCandidatesMap {...props} />;
  return <RecipeCandidatesMap {...props} />;
}

interface CandidatesMapProps {
  recipeId?: string;
  world: string;
  modSetHash?: string;
  /** The objective already named, when one is; drawn as the numbered pin. */
  place?: string | null;
  /** The way in, which sets the band the bases are judged by. */
  means?: string | null;
  /** Which pins answer a click: the objectives (the map step), the bases and the ground (the start step), or a mission's place's candidates. */
  pick: 'objective' | 'base' | 'place';
  /** The point the start step holds, drawn as the start marker. */
  chosenPoint?: [number, number] | null;
  onPickObjective?: (candidate: CandidateView) => void;
  onPickPoint?: (x: number, y: number, candidate: CandidateView | null) => void;
  /** `pick: 'place'`: the mission and the place (`camp`, `point:base`) whose candidates to show. */
  mission?: { code: string; placeId: string; worldSize: number };
  /** `pick: 'place'`: the candidate clicked. */
  onPickPlace?: (candidate: PlaceCandidateView) => void;
  /** `pick: 'place'`: the candidate already picked, highlighted. */
  pickedPlace?: PlaceCandidateView | null;
  /**
   * The world's named places as the catalogue read them, with the engine's own
   * kind of each (capital, city, village, hamlet): what a dot's size is told
   * from. Without them every dot is the medium one.
   */
  places?: PlaceView[];
  height?: number;
}

/** A generated mission's place and its kind's candidates (release D, D11.8). */
function PlaceCandidatesMap({
  world,
  mission,
  onPickPlace,
  pickedPlace,
  height = 520,
}: CandidatesMapProps) {
  const [found, setFound] = useState<PlaceCandidatesView | null>(null);
  const [failed, setFailed] = useState(false);
  const [relief, setRelief] = useState<Heightmap | null>(null);
  const code = mission?.code ?? '';
  const placeId = mission?.placeId ?? '';

  useEffect(() => {
    let live = true;
    setFailed(false);
    setFound(null);
    fetchPlaceCandidates(code, placeId)
      .then((c) => {
        if (live) setFound(c);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [code, placeId]);

  useEffect(() => {
    let live = true;
    fetchHeightmap(world)
      .then((map) => {
        if (live) setRelief(map);
      })
      .catch(() => {
        if (live) setRelief(null);
      });
    return () => {
      live = false;
    };
  }, [world]);

  const s = ca.script;
  if (failed) return <p className="notice warn small">{s.moveUnavailable}</p>;
  if (!found) return <p className="muted small">{s.moveLoading}</p>;
  const current = found.current;
  const others = found.candidates.filter(
    (c) => !current || c.x !== current[0] || c.y !== current[1],
  );
  if (others.length === 0) return <p className="muted small">{s.moveNone}</p>;
  const idOf = (c: PlaceCandidateView) => `cand:${c.x}:${c.y}`;
  const markers = others.map((c) => ({
    id: idOf(c),
    kind: 'icon' as const,
    symbol: 'mil_dot',
    colour: 'ColorEAST',
    position: [c.x, c.y],
    text: [c.label, c.variant].filter(Boolean).join(' · ') || found.kind,
  }));
  const objectives = current
    ? [{ id: 'current', type: 'objective', title: s.moveCurrent, position: current }]
    : [];
  function select(selection: Selection) {
    if (selection.kind !== 'marker') return;
    const c = others.find((x) => idOf(x) === selection.id);
    if (c) onPickPlace?.(c);
  }
  return (
    <div className="candidates-map">
      <p className="muted small">{s.moveCount(others.length, found.truncated)}</p>
      <MissionMap
        world={world}
        worldSize={mission?.worldSize ?? 0}
        objectives={objectives}
        markers={markers}
        relief={relief}
        height={height}
        selected={pickedPlace ? { kind: 'marker', id: idOf(pickedPlace) } : null}
        onSelect={select}
      />
    </div>
  );
}

function RecipeCandidatesMap({
  recipeId,
  world,
  modSetHash,
  place,
  means,
  pick,
  chosenPoint,
  onPickObjective,
  onPickPoint,
  places,
  height = 520,
}: CandidatesMapProps) {
  const [found, setFound] = useState<CandidatesView | null>(null);
  const [failed, setFailed] = useState(false);
  const [relief, setRelief] = useState<Heightmap | null>(null);

  useEffect(() => {
    let live = true;
    setFailed(false);
    fetchCandidates(recipeId ?? '', world, modSetHash ?? '', place ?? undefined, means ?? undefined)
      .then((c) => {
        if (live) setFound(c);
      })
      .catch(() => {
        if (live) {
          setFound(null);
          setFailed(true);
        }
      });
    return () => {
      live = false;
    };
  }, [recipeId, world, modSetHash, place, means]);

  // The surveyed ground, once per world: most of the catalogue has it now, and a
  // terrain without it keeps the picture the platform has of it.
  useEffect(() => {
    let live = true;
    setRelief(null);
    fetchHeightmap(world)
      .then((map) => {
        if (live) setRelief(map);
      })
      .catch(() => {
        if (live) setRelief(null);
      });
    return () => {
      live = false;
    };
  }, [world]);

  const t = ca.missionUi.candidates;
  if (failed) return <p className="notice warn small">{t.unavailable}</p>;
  if (!found) return <p className="muted small">{t.loading}</p>;

  const objectiveId = (c: CandidateView) => `obj:${c.name}:${c.x}:${c.y}`;
  const baseId = (c: CandidateView) => `base:${c.kind}:${c.x}:${c.y}`;
  const chosen = found.objective;
  // On the map step a place is a dot (bigger for a bigger place, its name on
  // hover) and the one named is a ringed dot with its name always shown; on the
  // start step the named objective stays the numbered pin and the rest grey dots.
  // A candidate is the catalogue's place itself, so its point finds its kind.
  const typeAt = new Map((places ?? []).map((p) => [`${p.x}:${p.y}`, p.type]));
  // The most houses the survey counted in any town of the map: what a town's own count is read against.
  const most = Math.max(0, ...found.objectives.map((c) => c.houses ?? 0));
  const named = pick === 'objective' ? chosen : null;
  const markers = [
    ...found.objectives
      .filter((c) => !chosen || c.x !== chosen.x || c.y !== chosen.y)
      .map((c) => ({
        id: objectiveId(c),
        kind: 'icon' as const,
        symbol:
          c.kind === 'settlement'
            ? dotSizeOf(typeAt.get(`${c.x}:${c.y}`), c.houses, most)
            : dotSizeOf(null),
        colour: pick === 'objective' ? 'ColorOrange' : 'ColorGrey',
        position: [c.x, c.y],
        text: c.name,
      })),
    ...(named
      ? [
          {
            id: objectiveId(named),
            kind: 'icon' as const,
            symbol: 'place_ring',
            colour: 'ColorOrange',
            position: [named.x, named.y],
            text: named.name,
          },
        ]
      : []),
    ...found.bases.map((c) => ({
      id: baseId(c),
      kind: 'icon' as const,
      symbol: c.kind === 'ship' ? 'mil_join' : c.kind === 'camp' ? 'mil_dot' : 'mil_flag',
      colour: c.inReach ? 'ColorWEST' : 'ColorGrey',
      position: [c.x, c.y],
      text: `${t.baseKind(c.kind)} · ${c.name}${c.inReach ? '' : ` (${t.farFromObjective})`}`,
    })),
  ];
  const objectives =
    chosen && !named
      ? [
          {
            id: 'chosen',
            type: 'objective',
            title: `${t.objective}: ${chosen.name}`,
            position: [chosen.x, chosen.y],
          },
        ]
      : [];

  function select(selection: Selection) {
    if (selection.kind === 'marker') {
      const asObjective = found!.objectives.find((c) => objectiveId(c) === selection.id);
      if (asObjective && pick === 'objective') onPickObjective?.(asObjective);
      const asBase = found!.bases.find((c) => baseId(c) === selection.id);
      if (asBase && pick === 'base') onPickPoint?.(asBase.x, asBase.y, asBase);
    }
  }

  const counts =
    pick === 'objective'
      ? t.objectivesCount(found.objectives.length, found.needs)
      : t.basesCount(found.bases.filter((b) => b.inReach).length, found.bases.length);

  return (
    <div className="candidates-map">
      <p className="muted small">
        {counts} {pick === 'objective' ? t.clickObjective : t.clickBase}
      </p>
      <MissionMap
        world={world}
        worldSize={found.worldSize}
        objectives={objectives}
        markers={markers}
        start={chosenPoint ?? undefined}
        relief={relief}
        height={height}
        selected={named ? { kind: 'marker', id: objectiveId(named) } : null}
        onSelect={select}
        onPlace={
          pick === 'base' ? (x, y) => onPickPoint?.(Math.round(x), Math.round(y), null) : undefined
        }
      />
    </div>
  );
}
